/**
 * Feature discovery, ordering and the per-frame host (plan §2.4). Every `src/features/<name>/feature.ts`
 * default-exports a `GameFeature`; this module finds them with `import.meta.glob`, so adding a feature
 * never touches `main.ts` or `src/app/**`.
 */
import { logger } from '../core/logger'
import type { FrameInfo, GameContext, GameFeature } from './feature'

/** Module map as `import.meta.glob(..., { eager: true })` returns it: path → module. */
export type FeatureModules = Record<string, { default: GameFeature }>

function isFeature(value: unknown): value is GameFeature {
  if (typeof value !== 'object' || value === null) return false
  const f = value as Partial<GameFeature>
  return typeof f.name === 'string' && f.name.length > 0 && typeof f.order === 'number' && typeof f.init === 'function'
}

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}

/** Validates and sorts the features of a module map (order, then name). Throws naming the bad module. */
export function collectFeatures(modules: FeatureModules): GameFeature[] {
  const list: GameFeature[] = []
  const names = new Set<string>()
  for (const path of Object.keys(modules).sort()) {
    const f: unknown = modules[path].default
    if (!isFeature(f)) throw new Error(`${path} must default-export a GameFeature { name, order, init }`)
    if (names.has(f.name)) throw new Error(`Duplicate feature name "${f.name}" (${path})`)
    names.add(f.name)
    list.push(f)
  }
  return list.sort((a, b) => a.order - b.order || (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
}

/** Every feature under src/features/, sorted. */
export function discoverFeatures(): GameFeature[] {
  return collectFeatures(import.meta.glob<{ default: GameFeature }>('../features/*/feature.ts', { eager: true }))
}

/** Runs every `init` in order, one at a time. A failure names the feature and keeps the original as `cause`. */
export async function initFeatures(features: readonly GameFeature[], ctx: GameContext): Promise<void> {
  for (let i = 0; i < features.length; i++) {
    const f = features[i]
    try {
      await f.init(ctx)
    } catch (err: unknown) {
      throw new Error(`Feature "${f.name}" failed to initialise: ${messageOf(err)}`, { cause: err })
    }
    logger.debug(`Feature ${f.name} ready`)
  }
}

export interface FeatureHost {
  readonly features: readonly GameFeature[]
  /** Calls every `afterTick` in order. Allocation-free. */
  afterTick(ctx: GameContext): void
  /** Calls every `frame` in order. Allocation-free. */
  frame(ctx: GameContext, f: FrameInfo): void
  /** Additive (M3 R1): calls every `overlay` in order, after `post.render()`. Allocation-free. */
  overlay(ctx: GameContext, f: FrameInfo): void
  /** Disposes in reverse order; a failing dispose is logged and the rest still run. */
  dispose(): void
}

export function createFeatureHost(features: readonly GameFeature[]): FeatureHost {
  // Pre-filtered hook lists, so the hot loop never checks for missing hooks (D-014).
  const ticking = features.filter((f) => f.afterTick !== undefined)
  const framing = features.filter((f) => f.frame !== undefined)
  const overlaying = features.filter((f) => f.overlay !== undefined)
  return {
    features,
    afterTick(ctx: GameContext): void {
      for (let i = 0; i < ticking.length; i++) {
        const f = ticking[i]
        try {
          f.afterTick?.(ctx)
        } catch (err: unknown) {
          throw new Error(`Feature "${f.name}" failed in afterTick: ${messageOf(err)}`, { cause: err })
        }
      }
    },
    frame(ctx: GameContext, info: FrameInfo): void {
      for (let i = 0; i < framing.length; i++) {
        const f = framing[i]
        try {
          f.frame?.(ctx, info)
        } catch (err: unknown) {
          throw new Error(`Feature "${f.name}" failed in frame: ${messageOf(err)}`, { cause: err })
        }
      }
    },
    overlay(ctx: GameContext, info: FrameInfo): void {
      for (let i = 0; i < overlaying.length; i++) {
        const f = overlaying[i]
        try {
          f.overlay?.(ctx, info)
        } catch (err: unknown) {
          throw new Error(`Feature "${f.name}" failed in overlay: ${messageOf(err)}`, { cause: err })
        }
      }
    },
    dispose(): void {
      for (let i = features.length - 1; i >= 0; i--) {
        try {
          features[i].dispose?.()
        } catch (err: unknown) {
          logger.warn(`Feature "${features[i].name}" failed to dispose`, err)
        }
      }
    },
  }
}
